import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import FamilyCalendar from './FamilyCalendar';
import AppModal from './AppModal';
import HelpButton from '../tours/HelpButton';
import useAutoTour from '../tours/useAutoTour';
import { showErrorToast } from '../utils/toast';   // Block 233
import { messageFromError } from '../utils/apiError';   // Block 233
import { useDeepLinkTarget } from '../utils/deepLink';   // UI-4, decision 56
import { Reveal } from '../ui';   // inline forms open where the person is looking (2026-10-21)

const ROLE_LABEL = { owner: 'You', adult: 'Adult', child: 'Child' };
const ROLE_COLOR = { owner: '#667eea', adult: '#059669', child: '#d97706' };

function calcAge(dob) {
  if (!dob) return null;
  const bd = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - bd.getFullYear();
  const hadBirthday =
    today.getMonth() > bd.getMonth() ||
    (today.getMonth() === bd.getMonth() && today.getDate() >= bd.getDate());
  if (!hadBirthday) age -= 1;
  return age;
}

function formatDob(dob) {
  if (!dob) return '';
  return new Date(dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

const FONT = "'Raleway', sans-serif";

const BLANK = {
  name: '', role: 'adult', dob: '', allergies: '', schools: '', workplace: '',
  clubs: '', gpPractice: '', dentist: '', medication: '', notes: '',
  kidsWordsToAvoid: '', kidsBannedPhrases: '', kidsAideyContext: '',
  accessOption: 'none', inviteEmail: '', childUsername: '', childPassword: '', childConfirmPassword: '', accessConsent: false,
};

const BLANK_INVITE = { memberId: '', memberName: '', memberRole: '', email: '', consent: false, submitting: false, error: '' };
// accessModal: null | { memberId, memberName, memberRole, step: 'choose'|'email'|'username', email, username, password, confirmPassword, consent, submitting, error }
const BLANK_ACCESS = { memberId: '', memberName: '', memberRole: '', step: 'choose', email: '', username: '', password: '', confirmPassword: '', consent: false, submitting: false, error: '' };

export default function Family({ googleEvents = [], icalEvents = [], outlookEvents = [] }) {
  useAutoTour('family');
  const { activeWorkspaceId, workspaces } = useCurrentUser();
  const isPersonalWs = (workspaces || []).find(w => w.id === activeWorkspaceId)?.type === 'personal';
  const [activeTab, setActiveTab] = useState('members');
  // UI-4, decision 56: the sidebar's "Calendar" (Personal) opens this page on its calendar tab.
  const [tabLink, clearTabLink] = useDeepLinkTarget('family_tab');
  useEffect(() => { if (tabLink) { setActiveTab(tabLink); clearTabLink(); } }, [tabLink, clearTabLink]);

  const [members, setMembers] = useState([]);
  const [invitesEnabled, setInvitesEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmMemberId, setConfirmMemberId] = useState(null);
  const [confirmRewardId,  setConfirmRewardId]  = useState(null);
  const [inviteModal, setInviteModal] = useState(null); // null or BLANK_INVITE
  const [accessModal, setAccessModal] = useState(null); // null or BLANK_ACCESS (new two-path access flow)
  const [resetModal, setResetModal] = useState(null);   // null or { memberId, memberName, password, confirmPassword, submitting, error }
  // seatModal: null | { memberName, code, current, included, purchased, pendingAction:'email'|'username', pendingData, purchasing, error }
  const [seatModal, setSeatModal] = useState(null);
  const [actioningId, setActioningId] = useState(null); // memberId being suspended/resumed
  const [gamification, setGamification] = useState(null);
  const [rewardForm, setRewardForm] = useState(null); // null or { name, emoji, starCost, memberId }
  const [claimMsg, setClaimMsg] = useState('');
  const [showRewardsPanel, setShowRewardsPanel] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId || !isPersonalWs) return;
    setLoading(true);
    setError('');
    try {
      const [res, gRes] = await Promise.all([
        api('/api/household'),
        api('/api/household/gamification'),
      ]);
      const data = await res.json();
      if (Array.isArray(data)) {
        setMembers(data);
        setInvitesEnabled(false);
      } else {
        setMembers(data.members || []);
        setInvitesEnabled(data.invitesEnabled || false);
      }
      if (gRes.ok) setGamification(await gRes.json());
    } catch (err) {
      setError(messageFromError(err, 'Could not load your household.'));
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId, isPersonalWs]);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setEditingId(null);
    setForm(BLANK);
    setSaveError('');
    setShowForm(true);
  }

  function openEdit(m) {
    setEditingId(m.id);
    setForm({
      name:       m.name       || '',
      role:       m.role       || 'adult',
      dob:        m.dob ? m.dob.slice(0, 10) : '',
      allergies:  m.allergies  || '',
      schools:    m.schools    || '',
      workplace:  m.workplace  || '',
      clubs:      m.clubs      || '',
      gpPractice: m.gpPractice || '',
      dentist:    m.dentist    || '',
      medication: m.medication || '',
      notes:            m.notes            || '',
      kidsWordsToAvoid: m.kidsWordsToAvoid || '',
      kidsBannedPhrases: m.kidsBannedPhrases || '',
      kidsAideyContext: m.kidsAideyContext  || '',
    });
    setSaveError('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(BLANK);
    setSaveError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { setSaveError('Name is required.'); return; }

    // Validate access fields before hitting the API
    if (!editingId && form.accessOption === 'email') {
      if (!form.inviteEmail.trim()) { setSaveError('Email address is required.'); return; }
      if (!form.accessConsent) { setSaveError('Consent is required to grant app access.'); return; }
    }
    if (!editingId && form.accessOption === 'username') {
      if (!form.childUsername.trim()) { setSaveError('Username is required.'); return; }
      if (!/^[a-z0-9_.-]{3,30}$/.test(form.childUsername)) { setSaveError('Username: 3–30 characters, letters/numbers/_ . - only.'); return; }
      if (form.childPassword.length < 8) { setSaveError('Password must be at least 8 characters.'); return; }
      if (form.childPassword !== form.childConfirmPassword) { setSaveError('Passwords do not match.'); return; }
      if (!form.accessConsent) { setSaveError('Parental consent is required.'); return; }
    }

    setSaving(true);
    setSaveError('');
    // Block 233: declared BEFORE the try, not inside it — a `let` in the try block is a
    // sibling scope to the catch and is not visible there. The catch needs it to tell
    // "the profile never saved" from "the profile saved and only access failed".
    let memberId = null;
    try {
      const isChild = form.role === 'child';
      const payload = {
        name:       form.name.trim(),
        role:       form.role,
        dob:        form.dob        || null,
        allergies:  form.allergies  || null,
        schools:    isChild  ? (form.schools   || null) : null,
        workplace:  !isChild ? (form.workplace || null) : null,
        clubs:      form.clubs      || null,
        gpPractice: form.gpPractice || null,
        dentist:    form.dentist    || null,
        medication: form.medication || null,
        notes:             form.notes             || null,
        kidsWordsToAvoid:  isChild ? (form.kidsWordsToAvoid  || null) : null,
        kidsBannedPhrases: isChild ? (form.kidsBannedPhrases || null) : null,
        kidsAideyContext:  isChild ? (form.kidsAideyContext  || null) : null,
      };
      const url    = editingId ? `/api/household/${editingId}` : '/api/household';
      const method = editingId ? 'PATCH' : 'POST';
      const res    = await api(url, { method, body: JSON.stringify(payload) });
      const data = await res.json();

      // Grant access if requested (add form only)
      let pendingSeat = null;
      if (!editingId && form.accessOption !== 'none') {
        memberId = data.id;
        let accessRes;
        if (form.accessOption === 'email') {
          accessRes = await api('/api/household/invite', {
            method: 'POST',
            body: JSON.stringify({ memberId, email: form.inviteEmail.trim(), consentConfirmed: true }),
          });
        } else {
          accessRes = await api(`/api/household/members/${memberId}/create-child-login`, {
            method: 'POST',
            body: JSON.stringify({ username: form.childUsername, password: form.childPassword, consentConfirmed: true }),
          });
        }
      }

      await load();
      closeForm();
      if (pendingSeat) setSeatModal(pendingSeat);
    } catch (err) {
      // Block 233: both branches that used to live here — `accessRes.status === 402`
      // and `!accessRes.ok` — sat after `await api(...)` and were unreachable.
      //
      // This matters more than the wording, because BY THIS POINT THE PROFILE IS
      // ALREADY CREATED: the member POST succeeded and only the follow-up access grant
      // failed. So the catch reported "Could not save those details", which was simply
      // untrue, and because neither dead branch ran, load() never fired and the new
      // member did not appear in the list despite existing on the server.
      //
      // The seat case is the third and last of the 402 upsells in this file.
      const body = err?.body;
      if (err?.status === 402 && body && memberId) {
        await load();
        closeForm();
        setSeatModal({
          memberName: form.name.trim(),
          code: body.code,
          current: body.current,
          included: body.included,
          purchased: body.purchased || 0,
          pendingAction: form.accessOption,
          pendingData: form.accessOption === 'email'
            ? { memberId, email: form.inviteEmail.trim() }
            : { memberId, username: form.childUsername, password: form.childPassword },
          purchasing: false,
          error: '',
        });
        return;
      }
      if (memberId) {
        // The profile saved; only the access grant failed. Say so, and reload so the
        // member the user just created is actually on screen.
        setSaveError(messageFromError(err, 'Profile created, but granting access failed. Use "Give app access" from the member card.'));
        await load();
        return;
      }
      setSaveError(messageFromError(err, 'Could not save those details.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    setDeletingId(id);
    try {
      const res = await api(`/api/household/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      showErrorToast(messageFromError(err, 'Could not remove that person.'));
    } finally {
      setDeletingId(null);
    }
  }

  function openInvite(m) {
    setInviteModal({ ...BLANK_INVITE, memberId: m.id, memberName: m.name, memberRole: m.role });
  }

  function closeInvite() {
    setInviteModal(null);
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!inviteModal.consent) return;
    setInviteModal(prev => ({ ...prev, submitting: true, error: '' }));
    try {
      const res = await api('/api/household/invite', {
        method: 'POST',
        body: JSON.stringify({
          memberId: inviteModal.memberId,
          email: inviteModal.email.trim(),
          consentConfirmed: true,
        }),
      });
      closeInvite();
      await load();
    } catch (err) {
      setInviteModal(prev => ({ ...prev, submitting: false, error: messageFromError(err, 'Could not send that invitation.') }));
    }
  }

  async function handleSuspend(id) {
    setActioningId(id);
    try {
      const res = await api(`/api/household/members/${id}/suspend`, { method: 'POST' });
      await load();
    } catch (err) {
      showErrorToast(messageFromError(err, 'Could not pause their access.'));
    } finally {
      setActioningId(null);
    }
  }

  async function handleResume(id) {
    setActioningId(id);
    try {
      const res = await api(`/api/household/members/${id}/resume`, { method: 'POST' });
      await load();
    } catch (err) {
      showErrorToast(messageFromError(err, 'Could not restore their access.'));
    } finally {
      setActioningId(null);
    }
  }

  function openAccess(m) {
    setAccessModal({ ...BLANK_ACCESS, memberId: m.id, memberName: m.name, memberRole: m.role });
  }

  async function handleCreateChildLogin(e) {
    e.preventDefault();
    if (!accessModal.consent) return;
    const { username, password, confirmPassword } = accessModal;
    if (password !== confirmPassword) {
      setAccessModal(prev => ({ ...prev, error: 'Passwords do not match.' }));
      return;
    }
    setAccessModal(prev => ({ ...prev, submitting: true, error: '' }));
    try {
      const res = await api(`/api/household/members/${accessModal.memberId}/create-child-login`, {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), password, consentConfirmed: true }),
      });
      setAccessModal(null);
      await load();
    } catch (err) {
      // Block 233: this 402 branch used to sit above, testing res.status after
      // await api(...) — unreachable, because api() throws on a non-2xx. So the
      // "you need another seat" upsell had never opened once; the 402 fell through
      // to the error line instead.
      const d = err?.body;
      if (err?.status === 402 && d) {
        setSeatModal({ memberName: accessModal.memberName, code: d.code, current: d.current, included: d.included, purchased: d.purchased || 0, pendingAction: 'username', pendingData: { memberId: accessModal.memberId, username: username.trim(), password }, purchasing: false, error: '' });
        setAccessModal(null);
        return;
      }
      setAccessModal(prev => ({ ...prev, submitting: false, error: messageFromError(err, 'Could not create that login.') }));
    }
  }

  async function handleEmailInviteFromAccess(e) {
    e.preventDefault();
    if (!accessModal.consent) return;
    setAccessModal(prev => ({ ...prev, submitting: true, error: '' }));
    try {
      const res = await api('/api/household/invite', {
        method: 'POST',
        body: JSON.stringify({ memberId: accessModal.memberId, email: accessModal.email.trim(), consentConfirmed: true }),
      });
      setAccessModal(null);
      await load();
    } catch (err) {
      // Block 233: same unreachable 402 as the username path above — the seat upsell
      // never opened, and running out of seats read as a failed invitation.
      const d = err?.body;
      if (err?.status === 402 && d) {
        setSeatModal({ memberName: accessModal.memberName, code: d.code, current: d.current, included: d.included, purchased: d.purchased || 0, pendingAction: 'email', pendingData: { memberId: accessModal.memberId, email: accessModal.email }, purchasing: false, error: '' });
        setAccessModal(null);
        return;
      }
      setAccessModal(prev => ({ ...prev, submitting: false, error: messageFromError(err, 'Could not send that invitation.') }));
    }
  }

  async function handleResetChildPassword(e) {
    e.preventDefault();
    const { password, confirmPassword } = resetModal;
    if (password !== confirmPassword) {
      setResetModal(prev => ({ ...prev, error: 'Passwords do not match.' }));
      return;
    }
    if (password.length < 8) {
      setResetModal(prev => ({ ...prev, error: 'Password must be at least 8 characters.' }));
      return;
    }
    setResetModal(prev => ({ ...prev, submitting: true, error: '' }));
    try {
      const res = await api(`/api/household/members/${resetModal.memberId}/reset-child-password`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      const d = await res.json();
      setResetModal(null);
    } catch (err) {
      setResetModal(prev => ({ ...prev, submitting: false, error: messageFromError(err, 'Could not reset that password.') }));
    }
  }

  async function purchaseSeat() {
    setSeatModal(prev => ({ ...prev, purchasing: true, error: '' }));
    try {
      const res = await api('/api/household/family-seats/purchase', { method: 'POST' });
      const d = await res.json();
      const { pendingAction, pendingData } = seatModal;
      setSeatModal(null);
      if (pendingAction === 'email') {
        const r2 = await api('/api/household/invite', { method: 'POST', body: JSON.stringify({ ...pendingData, consentConfirmed: true }) });
        if (!r2.ok) { const d2 = await r2.json(); showErrorToast(d2.error || 'Seat added but invite failed. Please try again.'); }
      } else if (pendingAction === 'username') {
        const r2 = await api(`/api/household/members/${pendingData.memberId}/create-child-login`, { method: 'POST', body: JSON.stringify({ username: pendingData.username, password: pendingData.password, consentConfirmed: true }) });
        if (!r2.ok) { const d2 = await r2.json(); showErrorToast(d2.error || 'Seat added but login creation failed. Please try again.'); }
      }
      await load();
    } catch (err) {
      setSeatModal(prev => ({ ...prev, purchasing: false, error: messageFromError(err, 'Could not add that seat.') }));
    }
  }

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.75rem',
    border: '1px solid #e0e0e0', borderRadius: 8, fontSize: '0.9rem',
    fontFamily: FONT, color: '#333', background: '#fff',
    marginBottom: '0.75rem',
  };
  const labelStyle = {
    display: 'block', fontSize: '0.78rem', fontWeight: 600,
    color: '#666', marginBottom: '0.25rem', fontFamily: FONT,
  };

  const isChild = form.role === 'child';

  // ── Gamification helpers ──────────────────────────────────────────────────

  const gByMember = {};
  for (const m of (gamification?.members || [])) gByMember[m.id] = m;

  async function handleClaim(rewardId, memberId) {
    setClaimMsg('');
    try {
      const res = await api(`/api/household/rewards/${rewardId}/redeem`, {
        method: 'POST',
        body:   JSON.stringify({ memberId }),
      });
      const data = await res.json();
      setClaimMsg('Asked! Waiting for a grown-up to approve 🎉');
      await load();
    } catch (err) { setClaimMsg(messageFromError(err, 'Could not send that request.')); }
  }

  async function handleResolve(redemptionId, action) {
    try {
      await api(`/api/household/redemptions/${redemptionId}`, {
        method: 'PATCH',
        body:   JSON.stringify({ action }),
      });
      await load();
    } catch { setError('Could not update redemption.'); }
  }

  async function handleAddReward(e) {
    e.preventDefault();
    if (!rewardForm) return;
    const body = {
      name:     rewardForm.name.trim(),
      emoji:    rewardForm.emoji || null,
      starCost: parseInt(rewardForm.starCost, 10) || 1,
      householdMemberId: rewardForm.memberId || null,
    };
    const res = await api('/api/household/rewards', { method: 'POST', body: JSON.stringify(body) });
    setRewardForm(null);
    await load();
  }

  async function handleDeleteReward(id) {
    await api(`/api/household/rewards/${id}`, { method: 'DELETE' });
    await load();
  }

  // ── Child dashboard (when a household child member is viewing) ─────────────

  if (gamification?.viewAs === 'child' && gamification.members.length > 0) {
    const me = gamification.members[0];
    const pending = (gamification.pendingRedemptions || []).filter(r => r.householdMember?.id === me.id);
    return (
      <div style={{ maxWidth: 400, margin: '0 auto', padding: '1.25rem 1rem', fontFamily: FONT }}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.2rem', fontWeight: 700, color: '#222' }}>
          Hi {me.name}! 👋
        </h2>

        {/* Stars + streak */}
        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 12, padding: '1rem', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32 }}>⭐</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#d97706' }}>{me.available}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>AVAILABLE</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{me.earned} total earned</div>
            </div>
            {me.streak > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32 }}>🔥</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#ef4444' }}>{me.streak}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>DAY STREAK</div>
                {me.bestStreak > me.streak && <div style={{ fontSize: 11, color: '#6b7280' }}>Best: {me.bestStreak}</div>}
              </div>
            )}
          </div>
        </div>

        {/* Pending redemptions */}
        {pending.map(r => (
          <div key={r.id} style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: 8, fontSize: 13 }}>
            {r.reward.emoji} {r.reward.name}, waiting for approval...
          </div>
        ))}

        {/* Claimable rewards */}
        {claimMsg && <p style={{ fontSize: 13, color: '#059669', fontWeight: 600, margin: '0 0 8px' }}>{claimMsg}</p>}
        {me.rewards.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Rewards</div>
            {me.rewards.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, color: '#374151' }}>{r.emoji} {r.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>⭐ {r.starCost}</span>
                  <button
                    onClick={() => handleClaim(r.id, me.id)}
                    disabled={!r.canAfford}
                    style={{ padding: '4px 12px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 700, cursor: r.canAfford ? 'pointer' : 'not-allowed', background: r.canAfford ? '#667eea' : '#e5e7eb', color: r.canAfford ? '#fff' : '#9ca3af' }}
                  >
                    Claim
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {me.rewards.length === 0 && (
          <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No rewards set up yet, ask a grown-up to add some!</p>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: activeTab === 'calendar' ? 900 : 640, margin: '0 auto', padding: '1.25rem 1rem', fontFamily: FONT }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.25rem', borderBottom: '2px solid #e5e7eb' }}>
        {[{ id: 'members', label: '👨‍👩‍👧‍👦 Members' }, { id: 'calendar', label: '🗓 Family Calendar' }].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.5rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontFamily: FONT, fontSize: '0.88rem', fontWeight: 600,
              color: activeTab === tab.id ? '#667eea' : '#6b7280',
              borderBottom: activeTab === tab.id ? '2px solid #667eea' : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'calendar' && (
        <FamilyCalendar
          googleEvents={googleEvents}
          icalEvents={icalEvents}
          outlookEvents={outlookEvents}
        />
      )}

      {activeTab === 'members' && <>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#222' }}>Family</h2>
          <HelpButton tourId="family" />
        </div>
        {!showForm && (
          <button
            data-tour-id="family-add-member"
            onClick={openAdd}
            style={{
              padding: '0.45rem 1rem', background: '#667eea', color: '#fff',
              border: 'none', borderRadius: 8, fontFamily: FONT, fontSize: '0.85rem',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            + Add member
          </button>
        )}
      </div>

      {invitesEnabled && (
        <p style={{ margin: '-0.75rem 0 1rem', fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 }}>
          Your Family plan covers 4 people: you plus 3 family members with app access. Extra members £3.50/month each. Unlimited profiles for birthdays, allergies and renewals are always free.
        </p>
      )}

      {error && (
        <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</p>
      )}

      {/* Invite consent modal */}
      <AppModal open={!!inviteModal} onClose={closeInvite} ariaLabel="Invite family member to Aidey" maxWidth={440}>
        {inviteModal && (
          <>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 700, color: '#222' }}>
              Invite {inviteModal.memberName} to Aidey
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#555', lineHeight: 1.55 }}>
              This will send {inviteModal.memberName} an email with a link to create their own Aidey login. They will be able to use a limited set of Aidey features within your family workspace. You can pause their access at any time.
            </p>
            <form onSubmit={handleInvite}>
              <label style={labelStyle}>Their email address</label>
              <input
                type="email"
                required
                style={inputStyle}
                value={inviteModal.email}
                onChange={e => setInviteModal(prev => ({ ...prev, email: e.target.value }))}
                placeholder="e.g. name@example.com"
                autoFocus
              />

              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                cursor: 'pointer', marginBottom: '1rem',
              }}>
                <input
                  type="checkbox"
                  checked={inviteModal.consent}
                  onChange={e => setInviteModal(prev => ({ ...prev, consent: e.target.checked }))}
                  style={{ marginTop: '0.15rem', flexShrink: 0, accentColor: '#667eea', width: 15, height: 15 }}
                />
                <span style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>
                  I confirm {inviteModal.memberName}'s date of birth is correct and I consent to this account.
                </span>
              </label>

              {inviteModal.error && (
                <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0 0 0.75rem', fontWeight: 600 }}>
                  {inviteModal.error}
                </p>
              )}

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="submit"
                  disabled={!inviteModal.consent || inviteModal.submitting}
                  style={{
                    flex: 1, padding: '0.6rem', background: '#667eea', color: '#fff',
                    border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: (!inviteModal.consent || inviteModal.submitting) ? 'not-allowed' : 'pointer',
                    opacity: (!inviteModal.consent || inviteModal.submitting) ? 0.55 : 1,
                  }}
                >
                  {inviteModal.submitting ? 'Sending…' : 'Send invite'}
                </button>
                <button
                  type="button"
                  onClick={closeInvite}
                  disabled={inviteModal.submitting}
                  style={{
                    padding: '0.6rem 1.1rem', background: '#f0f0f0', color: '#555',
                    border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600,
                    fontSize: '0.9rem', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </AppModal>

      {/* ── Access modal: choose email invite OR username creation ── */}
      <AppModal open={!!accessModal} onClose={() => setAccessModal(null)} ariaLabel="Give family member app access" maxWidth={460}>
        {accessModal && (
          <>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', fontWeight: 700, color: '#222' }}>
              Give {accessModal.memberName} app access
            </h3>

            {accessModal.step === 'choose' && (
              <>
                <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#555', lineHeight: 1.55 }}>
                  How would you like {accessModal.memberName} to sign in?
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  <button
                    onClick={() => setAccessModal(prev => ({ ...prev, step: 'email' }))}
                    style={{ padding: '0.85rem 1rem', background: '#f0f4ff', border: '1.5px solid #c7d2fe', borderRadius: 10, cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#3730a3', marginBottom: '0.15rem' }}>They have an email address</div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Send them an invite link to set up their own login</div>
                  </button>
                  <button
                    onClick={() => setAccessModal(prev => ({ ...prev, step: 'username' }))}
                    style={{ padding: '0.85rem 1rem', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#166534', marginBottom: '0.15rem' }}>Create a username instead</div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>You set a username and password, great for children without email</div>
                  </button>
                </div>
                <button type="button" onClick={() => setAccessModal(null)} style={{ padding: '0.5rem 1rem', background: '#f0f0f0', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', color: '#555' }}>Cancel</button>
              </>
            )}

            {accessModal.step === 'email' && (
              <form onSubmit={handleEmailInviteFromAccess}>
                <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#555', lineHeight: 1.55 }}>
                  {accessModal.memberName} will receive an email with a link to set their password and log in.
                </p>
                <label style={labelStyle}>Their email address</label>
                <input type="email" required style={inputStyle} value={accessModal.email} onChange={e => setAccessModal(prev => ({ ...prev, email: e.target.value }))} placeholder="name@example.com" autoFocus />
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', marginBottom: '1rem' }}>
                  <input type="checkbox" checked={accessModal.consent} onChange={e => setAccessModal(prev => ({ ...prev, consent: e.target.checked }))} style={{ marginTop: '0.15rem', flexShrink: 0, accentColor: '#667eea', width: 15, height: 15 }} />
                  <span style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>I confirm {accessModal.memberName}'s date of birth is correct and I consent to this account.</span>
                </label>
                {accessModal.error && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0 0 0.75rem', fontWeight: 600 }}>{accessModal.error}</p>}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" disabled={!accessModal.consent || accessModal.submitting} style={{ flex: 1, padding: '0.6rem', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: (!accessModal.consent || accessModal.submitting) ? 'not-allowed' : 'pointer', opacity: (!accessModal.consent || accessModal.submitting) ? 0.55 : 1 }}>
                    {accessModal.submitting ? 'Sending…' : 'Send invite'}
                  </button>
                  <button type="button" onClick={() => setAccessModal(prev => ({ ...prev, step: 'choose', email: '', consent: false, error: '' }))} style={{ padding: '0.6rem 1rem', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>Back</button>
                </div>
              </form>
            )}

            {accessModal.step === 'username' && (
              <form onSubmit={handleCreateChildLogin}>
                <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#555', lineHeight: 1.55 }}>
                  {accessModal.memberName} will sign in with this username and password. Email notifications about this account will go to you.
                </p>
                <label style={labelStyle}>Username</label>
                <input type="text" required autoCapitalize="none" spellCheck="false" style={inputStyle} value={accessModal.username} onChange={e => setAccessModal(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, '') }))} placeholder="e.g. alex123" autoFocus />
                <label style={labelStyle}>Password</label>
                <input type="password" required autoComplete="new-password" style={inputStyle} value={accessModal.password} onChange={e => setAccessModal(prev => ({ ...prev, password: e.target.value }))} placeholder="At least 8 characters" />
                <label style={labelStyle}>Confirm password</label>
                <input type="password" required autoComplete="new-password" style={inputStyle} value={accessModal.confirmPassword} onChange={e => setAccessModal(prev => ({ ...prev, confirmPassword: e.target.value }))} placeholder="Repeat password" />
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', marginBottom: '1rem' }}>
                  <input type="checkbox" checked={accessModal.consent} onChange={e => setAccessModal(prev => ({ ...prev, consent: e.target.checked }))} style={{ marginTop: '0.15rem', flexShrink: 0, accentColor: '#059669', width: 15, height: 15 }} />
                  <span style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>I am {accessModal.memberName}'s parent or guardian and consent to creating this account.</span>
                </label>
                {accessModal.error && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0 0 0.75rem', fontWeight: 600 }}>{accessModal.error}</p>}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" disabled={!accessModal.consent || accessModal.submitting} style={{ flex: 1, padding: '0.6rem', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: (!accessModal.consent || accessModal.submitting) ? 'not-allowed' : 'pointer', opacity: (!accessModal.consent || accessModal.submitting) ? 0.55 : 1 }}>
                    {accessModal.submitting ? 'Creating…' : 'Create login'}
                  </button>
                  <button type="button" onClick={() => setAccessModal(prev => ({ ...prev, step: 'choose', username: '', password: '', confirmPassword: '', consent: false, error: '' }))} style={{ padding: '0.6rem 1rem', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>Back</button>
                </div>
              </form>
            )}
          </>
        )}
      </AppModal>

      {/* ── Seat limit modal ── */}
      <AppModal open={!!seatModal} onClose={() => setSeatModal(null)} ariaLabel="Family seat limit" maxWidth={440}>
        {seatModal && (
          <>
            {seatModal.code === 'FAMILY_SEAT_LIMIT' ? (
              <>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', fontWeight: 700, color: '#222' }}>Add another member</h3>
                <p style={{ margin: '0 0 1.25rem', fontSize: '0.88rem', color: '#555', lineHeight: 1.6 }}>
                  Your Family plan covers <strong>{seatModal.included + 1} people</strong>: you plus {seatModal.included} family members with app access. You have used {seatModal.current} of {seatModal.included + seatModal.purchased}.
                </p>
                <p style={{ margin: '0 0 1.25rem', fontSize: '0.88rem', color: '#374151', lineHeight: 1.6, background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '0.75rem 1rem' }}>
                  Adding <strong>{seatModal.memberName}</strong> costs <strong>£3.50/month</strong>, added to your subscription today with proration.
                </p>
                {seatModal.error && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0 0 0.75rem', fontWeight: 600 }}>{seatModal.error}</p>}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={purchaseSeat}
                    disabled={seatModal.purchasing}
                    style={{ flex: 1, padding: '0.65rem', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: seatModal.purchasing ? 'not-allowed' : 'pointer', opacity: seatModal.purchasing ? 0.55 : 1 }}
                  >
                    {seatModal.purchasing ? 'Adding…' : 'Add seat (£3.50/month)'}
                  </button>
                  <button onClick={() => setSeatModal(null)} disabled={seatModal.purchasing} style={{ padding: '0.65rem 1.1rem', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', fontWeight: 700, color: '#222' }}>Upgrade to add more members</h3>
                <p style={{ margin: '0 0 1.25rem', fontSize: '0.88rem', color: '#555', lineHeight: 1.6 }}>
                  Your Personal plan includes 1 family member with app access. To add more, upgrade to the Family plan.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <a href="/billing" style={{ flex: 1, padding: '0.65rem', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', textAlign: 'center', textDecoration: 'none', display: 'inline-block' }}>View plans</a>
                  <button onClick={() => setSeatModal(null)} style={{ padding: '0.65rem 1.1rem', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>Cancel</button>
                </div>
              </>
            )}
          </>
        )}
      </AppModal>

      {/* ── Reset child password modal ── */}
      <AppModal open={!!resetModal} onClose={() => setResetModal(null)} ariaLabel="Reset child password" maxWidth={400}>
        {resetModal && (
          <>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 700, color: '#222' }}>Reset {resetModal.memberName}'s password</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#555', lineHeight: 1.55 }}>Set a new password for {resetModal.memberName}. Tell them the new password after you save it.</p>
            <form onSubmit={handleResetChildPassword}>
              <label style={labelStyle}>New password</label>
              <input type="password" required autoComplete="new-password" style={inputStyle} value={resetModal.password} onChange={e => setResetModal(prev => ({ ...prev, password: e.target.value }))} placeholder="At least 8 characters" autoFocus />
              <label style={labelStyle}>Confirm new password</label>
              <input type="password" required autoComplete="new-password" style={inputStyle} value={resetModal.confirmPassword} onChange={e => setResetModal(prev => ({ ...prev, confirmPassword: e.target.value }))} placeholder="Repeat password" />
              {resetModal.error && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0 0 0.75rem', fontWeight: 600 }}>{resetModal.error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" disabled={resetModal.submitting} style={{ flex: 1, padding: '0.6rem', background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: resetModal.submitting ? 'not-allowed' : 'pointer', opacity: resetModal.submitting ? 0.55 : 1 }}>
                  {resetModal.submitting ? 'Saving…' : 'Save new password'}
                </button>
                <button type="button" onClick={() => setResetModal(null)} style={{ padding: '0.6rem 1rem', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </>
        )}
      </AppModal>

      {showForm && (
        <Reveal key={editingId || 'new'} style={{
          background: '#f9f9fb', border: '1px solid #e0e0e0', borderRadius: 12,
          padding: '1.25rem', marginBottom: '1.25rem',
        }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: '#333' }}>
            {editingId ? 'Edit member' : 'Add family member'}
          </h3>
          <form onSubmit={handleSave} noValidate>
            <label style={labelStyle}>Name</label>
            <input
              style={inputStyle} value={form.name} autoFocus
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="First name"
            />

            <label style={labelStyle}>Role</label>
            <select
              style={{ ...inputStyle }}
              value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              disabled={!!editingId && form.role === 'owner'}
            >
              {form.role === 'owner' && <option value="owner">You (owner)</option>}
              <option value="adult">Adult</option>
              <option value="child">Child</option>
            </select>

            <label style={labelStyle}>Date of birth</label>
            <input
              type="date" style={inputStyle} value={form.dob}
              onChange={e => setForm(f => ({ ...f, dob: e.target.value }))}
            />

            <label style={labelStyle}>Allergies</label>
            <input
              style={inputStyle} value={form.allergies}
              onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))}
              placeholder="e.g. peanuts, dairy"
            />

            {/* Role-conditional: school for children, workplace for adults/owner */}
            {isChild ? (
              <>
                <label style={labelStyle}>School / nursery</label>
                <input
                  style={inputStyle} value={form.schools}
                  onChange={e => setForm(f => ({ ...f, schools: e.target.value }))}
                  placeholder="e.g. St Mary's Primary"
                />
              </>
            ) : (
              <>
                <label style={labelStyle}>Place of work</label>
                <input
                  style={inputStyle} value={form.workplace}
                  onChange={e => setForm(f => ({ ...f, workplace: e.target.value }))}
                  placeholder="e.g. Acme Ltd, London"
                />
              </>
            )}

            <label style={labelStyle}>Clubs / activities</label>
            <input
              style={inputStyle} value={form.clubs}
              onChange={e => setForm(f => ({ ...f, clubs: e.target.value }))}
              placeholder="e.g. football, swimming"
            />

            <label style={labelStyle}>GP practice</label>
            <input
              style={inputStyle} value={form.gpPractice}
              onChange={e => setForm(f => ({ ...f, gpPractice: e.target.value }))}
              placeholder="e.g. The Elms Surgery"
            />

            <label style={labelStyle}>Dentist</label>
            <input
              style={inputStyle} value={form.dentist}
              onChange={e => setForm(f => ({ ...f, dentist: e.target.value }))}
              placeholder="e.g. Bright Smiles Dental"
            />

            <label style={labelStyle}>
              Medication
              <span style={{ fontWeight: 400, color: '#999', marginLeft: 6, fontSize: '0.72rem' }}>
                private, never shared with Aidey
              </span>
            </label>
            <input
              style={inputStyle} value={form.medication}
              onChange={e => setForm(f => ({ ...f, medication: e.target.value }))}
              placeholder="e.g. inhaler, antihistamine"
            />

            <label style={labelStyle}>Notes</label>
            <textarea
              style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Anything else Aidey should know"
            />

            {/* Kids' Aidey rules — child members only */}
            {isChild && (
              <div style={{ borderTop: '1px solid #e8e8e8', margin: '0.5rem 0 0.25rem', paddingTop: '0.85rem' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#555', marginBottom: '0.15rem', fontFamily: FONT }}>Kids' Aidey rules</div>
                <div style={{ fontSize: '0.78rem', color: '#aaa', marginBottom: '0.75rem', fontFamily: FONT }}>These are injected into Aidey's instructions when chatting as {form.name || 'this child'}.</div>

                <label style={labelStyle}>Words to avoid</label>
                <input
                  style={inputStyle}
                  value={form.kidsWordsToAvoid}
                  onChange={e => setForm(f => ({ ...f, kidsWordsToAvoid: e.target.value }))}
                  placeholder="e.g. stupid, idiot"
                />

                <label style={labelStyle}>Phrases never to use</label>
                <input
                  style={inputStyle}
                  value={form.kidsBannedPhrases}
                  onChange={e => setForm(f => ({ ...f, kidsBannedPhrases: e.target.value }))}
                  placeholder="e.g. giving up is okay, you can't do it"
                />

                <label style={labelStyle}>Family context for Aidey</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                  value={form.kidsAideyContext}
                  onChange={e => setForm(f => ({ ...f, kidsAideyContext: e.target.value }))}
                  placeholder="e.g. Max loves football and hates maths. Be encouraging about schoolwork."
                />
              </div>
            )}

            {/* App access — only shown when adding a new member */}
            {!editingId && (
              <div style={{ borderTop: '1px solid #e8e8e8', margin: '0.25rem 0 0.75rem', paddingTop: '0.85rem' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#555', marginBottom: '0.5rem', fontFamily: FONT }}>App access</div>
                {[
                  { value: 'none',     label: 'No access yet, just a profile' },
                  { value: 'email',    label: 'Invite by email' },
                  { value: 'username', label: 'Create a username' },
                ].map(({ value, label }) => (
                  <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', marginBottom: '0.4rem' }}>
                    <input
                      type="radio" name="accessOption" value={value}
                      checked={form.accessOption === value}
                      onChange={() => setForm(f => ({ ...f, accessOption: value }))}
                      style={{ accentColor: '#667eea', width: 15, height: 15, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#333', fontFamily: FONT }}>{label}</span>
                  </label>
                ))}

                {form.accessOption === 'email' && (
                  <>
                    <label style={{ ...labelStyle, marginTop: '0.5rem' }}>Their email address</label>
                    <input type="email" style={inputStyle} value={form.inviteEmail} onChange={e => setForm(f => ({ ...f, inviteEmail: e.target.value }))} placeholder="name@example.com" />
                  </>
                )}

                {form.accessOption === 'username' && (
                  <>
                    <label style={{ ...labelStyle, marginTop: '0.5rem' }}>Username</label>
                    <input type="text" autoCapitalize="none" spellCheck="false" style={inputStyle} value={form.childUsername} onChange={e => setForm(f => ({ ...f, childUsername: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, '') }))} placeholder="e.g. alex123" />
                    <label style={labelStyle}>Password</label>
                    <input type="password" autoComplete="new-password" style={inputStyle} value={form.childPassword} onChange={e => setForm(f => ({ ...f, childPassword: e.target.value }))} placeholder="At least 8 characters" />
                    <label style={labelStyle}>Confirm password</label>
                    <input type="password" autoComplete="new-password" style={inputStyle} value={form.childConfirmPassword} onChange={e => setForm(f => ({ ...f, childConfirmPassword: e.target.value }))} placeholder="Repeat password" />
                    <p style={{ margin: '-0.25rem 0 0.5rem', fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.4 }}>
                      Email notifications about this account will go to you.
                    </p>
                  </>
                )}

                {form.accessOption !== 'none' && (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', marginTop: '0.35rem', marginBottom: '0.25rem' }}>
                    <input type="checkbox" checked={form.accessConsent} onChange={e => setForm(f => ({ ...f, accessConsent: e.target.checked }))} style={{ marginTop: '0.15rem', flexShrink: 0, accentColor: '#667eea', width: 15, height: 15 }} />
                    <span style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>
                      {form.accessOption === 'username'
                        ? `I am ${form.name.trim() || 'this person'}'s parent or guardian and consent to creating this account.`
                        : `I confirm ${form.name.trim() || 'this person'}'s date of birth is correct and I consent to this account.`}
                    </span>
                  </label>
                )}
              </div>
            )}

            {saveError && (
              <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '-0.25rem 0 0.75rem' }}>{saveError}</p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="submit" disabled={saving}
                style={{
                  flex: 1, padding: '0.6rem', background: '#667eea', color: '#fff',
                  border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600,
                  fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Saving...' : editingId ? 'Save changes' : 'Add member'}
              </button>
              <button
                type="button" onClick={closeForm}
                style={{
                  padding: '0.6rem 1.1rem', background: '#f0f0f0', color: '#555',
                  border: 'none', borderRadius: 8, fontFamily: FONT, fontWeight: 600,
                  fontSize: '0.9rem', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Reveal>
      )}

      {!activeWorkspaceId && (
        <p style={{ color: '#aaa', fontSize: '0.9rem' }}>Connecting to workspace…</p>
      )}
      {activeWorkspaceId && loading && !members.length && (
        <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading...</p>
      )}

      {!loading && !members.length && !showForm && (
        <p style={{ color: '#888', fontSize: '0.9rem' }}>
          No family members added yet. Add them so Aidey can help plan around your household.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {members.map(m => {
          const age = calcAge(m.dob);
          const roleColor = ROLE_COLOR[m.role] || '#666';
          const isOwner = m.role === 'owner';
          const isChildCard = m.role === 'child';
          const canInvite = !isOwner && m.inviteStatus !== 'active' && m.inviteStatus !== 'pending';
          const canSuspend = invitesEnabled && !isOwner && m.inviteStatus === 'active';
          const canResume = invitesEnabled && !isOwner && m.inviteStatus === 'suspended';
          const canResetPassword = invitesEnabled && m.isChildAccount && m.inviteStatus === 'active';
          const isActioning = actioningId === m.id;

          return (
            <div
              key={m.id}
              style={{
                background: '#fff', border: '1px solid #e8e8e8', borderRadius: 12,
                padding: '1rem 1.1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                opacity: m.inviteStatus === 'suspended' ? 0.75 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: '#222' }}>{m.name}</span>
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem',
                      borderRadius: 20, background: roleColor + '18', color: roleColor,
                      letterSpacing: '0.03em',
                    }}>
                      {ROLE_LABEL[m.role] || m.role}
                    </span>
                    {age !== null && (
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>
                        {age} {age === 1 ? 'year' : 'years'} old
                      </span>
                    )}
                    {m.weeklyCompleted > 0 && (
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem',
                        borderRadius: 20, background: 'rgba(251,196,144,0.25)', color: '#7a4a10',
                        border: '1px solid #FBC490',
                      }}>
                        ⭐ {m.weeklyCompleted} this week
                      </span>
                    )}
                    {isChildCard && gByMember[m.id]?.available > 0 && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 20, background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' }}>
                        ⭐ {gByMember[m.id].available} stars
                      </span>
                    )}
                    {isChildCard && gByMember[m.id]?.streak > 0 && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 20, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>
                        🔥 {gByMember[m.id].streak}-day streak
                      </span>
                    )}
                    {m.inviteStatus === 'active' && !isOwner && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 20, background: '#d1fae5', color: '#065f46' }}>
                        {m.isChildAccount ? `@${m.childUsername}` : 'Login active'}
                      </span>
                    )}
                    {m.inviteStatus === 'pending' && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 20, background: '#fef3c7', color: '#92400e' }}>
                        Invite sent
                      </span>
                    )}
                    {m.inviteStatus === 'suspended' && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 20, background: '#fee2e2', color: '#991b1b' }}>
                        Access paused
                      </span>
                    )}
                  </div>
                  {m.dob && (
                    <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: '#aaa' }}>
                      Born {formatDob(m.dob)}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => openEdit(m)}
                    style={{
                      padding: '0.3rem 0.7rem', fontSize: '0.78rem', background: '#f0f0f0',
                      border: 'none', borderRadius: 6, cursor: 'pointer', color: '#555',
                      fontFamily: FONT, fontWeight: 600,
                    }}
                  >
                    Edit
                  </button>
                  {canInvite && (
                    <button
                      onClick={() => openAccess(m)}
                      style={{
                        padding: '0.3rem 0.7rem', fontSize: '0.78rem', background: '#ede9fe',
                        border: 'none', borderRadius: 6, cursor: 'pointer', color: '#5b21b6',
                        fontFamily: FONT, fontWeight: 600,
                      }}
                    >
                      Give app access
                    </button>
                  )}
                  {canResetPassword && (
                    <button
                      onClick={() => setResetModal({ memberId: m.id, memberName: m.name, password: '', confirmPassword: '', submitting: false, error: '' })}
                      style={{
                        padding: '0.3rem 0.7rem', fontSize: '0.78rem', background: '#f0fdf4',
                        border: 'none', borderRadius: 6, cursor: 'pointer', color: '#166534',
                        fontFamily: FONT, fontWeight: 600,
                      }}
                    >
                      Reset password
                    </button>
                  )}
                  {canSuspend && (
                    <button
                      onClick={() => handleSuspend(m.id)}
                      disabled={isActioning}
                      style={{
                        padding: '0.3rem 0.7rem', fontSize: '0.78rem', background: '#fff7ed',
                        border: 'none', borderRadius: 6, cursor: isActioning ? 'not-allowed' : 'pointer',
                        color: '#c2410c', fontFamily: FONT, fontWeight: 600,
                      }}
                    >
                      {isActioning ? '...' : 'Pause'}
                    </button>
                  )}
                  {canResume && (
                    <button
                      onClick={() => handleResume(m.id)}
                      disabled={isActioning}
                      style={{
                        padding: '0.3rem 0.7rem', fontSize: '0.78rem', background: '#f0fdf4',
                        border: 'none', borderRadius: 6, cursor: isActioning ? 'not-allowed' : 'pointer',
                        color: '#166534', fontFamily: FONT, fontWeight: 600,
                      }}
                    >
                      {isActioning ? '...' : 'Resume'}
                    </button>
                  )}
                  {!isOwner && (
                    confirmMemberId === m.id ? (
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <span style={{ fontSize: '0.78rem', color: '#dc2626', fontFamily: FONT }}>Remove?</span>
                        <button onClick={() => { setConfirmMemberId(null); handleDelete(m.id); }} disabled={deletingId === m.id} style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem', background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', color: '#dc2626', fontFamily: FONT, fontWeight: 700 }}>Yes</button>
                        <button onClick={() => setConfirmMemberId(null)} style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', color: '#6b7280', fontFamily: FONT }}>No</button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmMemberId(m.id)}
                        disabled={deletingId === m.id}
                        style={{
                          padding: '0.3rem 0.7rem', fontSize: '0.78rem', background: '#fff0f0',
                          border: 'none', borderRadius: 6, cursor: 'pointer', color: '#dc2626',
                          fontFamily: FONT, fontWeight: 600,
                        }}
                      >
                        {deletingId === m.id ? '...' : 'Remove'}
                      </button>
                    )
                  )}
                </div>
              </div>

              {(m.allergies || m.schools || m.workplace || m.clubs || m.gpPractice || m.dentist || m.notes) && (
                <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {m.allergies && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                      background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6,
                      padding: '0.25rem 0.6rem', fontSize: '0.78rem', color: '#c2410c',
                      fontWeight: 600, alignSelf: 'flex-start',
                    }}>
                      <span>⚠</span> Allergies: {m.allergies}
                    </div>
                  )}
                  {isChildCard && m.schools && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>
                      <span style={{ color: '#888' }}>School:</span> {m.schools}
                    </p>
                  )}
                  {!isChildCard && m.workplace && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>
                      <span style={{ color: '#888' }}>Work:</span> {m.workplace}
                    </p>
                  )}
                  {m.clubs && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>
                      <span style={{ color: '#888' }}>Activities:</span> {m.clubs}
                    </p>
                  )}
                  {m.gpPractice && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>
                      <span style={{ color: '#888' }}>GP:</span> {m.gpPractice}
                    </p>
                  )}
                  {m.dentist && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>
                      <span style={{ color: '#888' }}>Dentist:</span> {m.dentist}
                    </p>
                  )}
                  {m.notes && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#555', fontStyle: 'italic' }}>
                      {m.notes}
                    </p>
                  )}
                  {m.role === 'child' && m.kidsAideyContext && (
                    <p style={{ margin: '0.3rem 0 0', fontSize: '0.78rem', color: '#9a6a00', background: '#fef3c7', borderRadius: 6, padding: '0.3rem 0.5rem' }}>
                      <span style={{ fontWeight: 700 }}>Aidey context:</span> {m.kidsAideyContext}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Pending redemption approvals ── */}
      {(gamification?.pendingRedemptions || []).length > 0 && (
        <div style={{ marginTop: 20, background: '#fef9c3', border: '1px solid #fde047', borderRadius: 12, padding: '1rem 1.1rem' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#713f12', marginBottom: 8 }}>Reward requests waiting for you</div>
          {gamification.pendingRedemptions.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
              <span style={{ fontSize: 13, color: '#374151' }}>
                <strong>{r.householdMember?.name}</strong>: {r.reward.emoji || ''} {r.reward.name} ({r.reward.starCost} star{r.reward.starCost !== 1 ? 's' : ''})
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => handleResolve(r.id, 'approve')}
                  style={{ padding: '4px 12px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Approve
                </button>
                <button onClick={() => handleResolve(r.id, 'decline')}
                  style={{ padding: '4px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Rewards panel ── */}
      {gamification?.viewAs === 'owner' && (
        <div style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#222', fontFamily: FONT }}>Rewards</h3>
            {!rewardForm && (
              <button onClick={() => setRewardForm({ name: '', emoji: '', starCost: '5', memberId: '' })}
                style={{ padding: '0.3rem 0.8rem', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                + Add reward
              </button>
            )}
          </div>

          {/* Existing rewards */}
          {(gamification.members.flatMap(m => m.rewards || [])
            .filter((r, i, a) => a.findIndex(x => x.id === r.id) === i)
          ).map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.6rem 0.8rem', background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>{r.emoji || '🎁'}</span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#374151' }}>{r.name}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>⭐ {r.starCost}</span>
              {confirmRewardId === r.id ? (
                <>
                  <span style={{ fontSize: 11, color: '#b91c1c' }}>Remove?</span>
                  <button onClick={() => { handleDeleteReward(r.id); setConfirmRewardId(null); }} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff7f7', color: '#b91c1c', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>Yes</button>
                  <button onClick={() => setConfirmRewardId(null)} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: 11, cursor: 'pointer' }}>No</button>
                </>
              ) : (
                <button onClick={() => setConfirmRewardId(r.id)}
                  style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff7f7', color: '#b91c1c', fontSize: 11, cursor: 'pointer' }}>
                  ×
                </button>
              )}
            </div>
          ))}

          {/* Add reward form */}
          {rewardForm && (
            <form onSubmit={handleAddReward} style={{ background: '#f9f9fb', border: '1px solid #e0e0e0', borderRadius: 10, padding: '0.85rem', marginTop: 8 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <div style={{ flex: '0 0 52px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#666', marginBottom: 3, fontFamily: FONT }}>Emoji</label>
                  <input value={rewardForm.emoji} onChange={e => setRewardForm(p => ({ ...p, emoji: e.target.value }))}
                    style={{ width: '100%', padding: '0.45rem', border: '1px solid #e0e0e0', borderRadius: 7, fontSize: '1rem', fontFamily: FONT, textAlign: 'center' }}
                    placeholder="🎬" maxLength={2} />
                </div>
                <div style={{ flex: '2 1 150px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#666', marginBottom: 3, fontFamily: FONT }}>Name</label>
                  <input required value={rewardForm.name} onChange={e => setRewardForm(p => ({ ...p, name: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.45rem 0.6rem', border: '1px solid #e0e0e0', borderRadius: 7, fontSize: '0.9rem', fontFamily: FONT }}
                    placeholder="e.g. Movie night" autoFocus />
                </div>
                <div style={{ flex: '0 0 80px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#666', marginBottom: 3, fontFamily: FONT }}>Stars</label>
                  <input required type="number" min="1" value={rewardForm.starCost} onChange={e => setRewardForm(p => ({ ...p, starCost: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.45rem 0.6rem', border: '1px solid #e0e0e0', borderRadius: 7, fontSize: '0.9rem', fontFamily: FONT }} />
                </div>
              </div>
              {(gamification?.members || []).length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#666', marginBottom: 3, fontFamily: FONT }}>For (optional, leave blank for any child)</label>
                  <select value={rewardForm.memberId} onChange={e => setRewardForm(p => ({ ...p, memberId: e.target.value }))}
                    style={{ padding: '0.45rem 0.6rem', borderRadius: 7, border: '1px solid #e0e0e0', fontSize: '0.9rem', fontFamily: FONT }}>
                    <option value="">Any child</option>
                    {(gamification.members || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" style={{ padding: '0.45rem 1rem', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>Save</button>
                <button type="button" onClick={() => setRewardForm(null)} style={{ padding: '0.45rem 0.8rem', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 8, fontSize: '0.85rem', cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
              </div>
            </form>
          )}

          {(gamification.members.flatMap(m => m.rewards || []).filter((r, i, a) => a.findIndex(x => x.id === r.id) === i)).length === 0 && !rewardForm && (
            <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>No rewards set up yet. Add one and children can claim them with their stars.</p>
          )}
        </div>
      )}

      </>}
    </div>
  );
}
